import React, { useEffect, useMemo, useState } from 'react';
import {
  FaUserPlus,
  FaFilePrescription,
  FaRupeeSign,
  FaReceipt,
  FaSearch,
  FaTimes,
  FaRegCheckSquare,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { DateRangeFilter, getDefaultDateRange, StatCard } from '../../Components/UI';
import GraphCard from './GraphCard';
import { formatCurrency, formatNumber } from './chartTheme';
import useGraphMetrics from './graphData';
import { buildCards } from './graphCards';
import useGraphPreferences, { useWebsiteGraphPreferences } from './graphPreferences';
import ThreeDotsLoading from './visulizeLoading';

// Groups come back on every metric; this is only the order they appear in.
// A group the server adds later still renders, at the end.
const SECTION_ORDER = [
  'Activity over time',
  'Appointments',
  'Composition',
  'Patient profile',
  'Where patients come from',
  'References',
  'Diagnosis',
  'Practice and fees',
  'Referred tests',
  'Case studies and reviews',
  'Courier',
  'Alternative therapies',
  'Interactions',
];

const formatRangeLabel = (startDate, endDate) => {
  const pretty = (value) =>
    new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  if (!startDate && !endDate) return 'All time';
  if (!startDate) return `Up to ${pretty(endDate)}`;
  if (!endDate) return `From ${pretty(startDate)}`;
  return `${pretty(startDate)} – ${pretty(endDate)}`;
};

const Section = ({ title, children }) => (
  <section className="mt-6 first:mt-0">
    <h2 className="mb-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</h2>
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">{children}</div>
  </section>
);

const VisualizeBoard = () => {
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [query, setQuery] = useState('');
  const [pinnedOnly, setPinnedOnly] = useState(false);

  const { catalogue, metrics, failed, loading, error } = useGraphMetrics(dateRange);
  // "Show on dashboard" ticks, saved per clinic on the server.
  const {
    selected: pinned,
    loading: prefsLoading,
    error: prefsError,
    setOnDashboard,
    retainOnly,
  } = useGraphPreferences();
  // "Show on website" ticks: aggregate graphs published on the clinic website.
  const {
    selected: onWebsite,
    loading: websiteLoading,
    error: websiteError,
    eligible: websiteEligible,
    max: maxWebsiteCards,
    setOnDashboard: setOnWebsite,
    retainOnly: retainOnWebsite,
  } = useWebsiteGraphPreferences();

  const totals = useMemo(() => {
    const patients = metrics['totals.patients'] || {};
    const prescriptions = metrics['totals.prescriptions'] || {};
    const count = prescriptions.count || 0;
    const collected = prescriptions.value || 0;
    return {
      patients: patients.count || 0,
      prescriptions: count,
      collected,
      average: count ? Math.round(collected / count) : 0,
    };
  }, [metrics]);

  const allCards = useMemo(() => buildCards(catalogue, metrics), [catalogue, metrics]);

  // Once the catalogue is in, ticks for graphs the server no longer offers
  // (removed or renamed metrics) are dropped from the saved selection.
  useEffect(() => {
    if (prefsLoading || !catalogue.length || !allCards.length) return;
    const ids = new Set(allCards.map((card) => card.id));
    retainOnly((id) => ids.has(id));
  }, [prefsLoading, catalogue, allCards, retainOnly]);

  useEffect(() => {
    if (websiteLoading || !websiteEligible || !catalogue.length || !allCards.length) return;
    const ids = new Set(allCards.map((card) => card.id));
    retainOnWebsite((id) => ids.has(id) && websiteEligible.includes(id));
  }, [websiteLoading, websiteEligible, catalogue, allCards, retainOnWebsite]);

  const websiteFull = maxWebsiteCards > 0 && onWebsite.length >= maxWebsiteCards;

  const visibleCards = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return allCards.filter((card) => {
      if (pinnedOnly && !pinned.includes(card.id)) return false;
      if (!terms.length) return true;
      const haystack = `${card.title} ${card.subtitle} ${card.group} ${card.keywords}`.toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  }, [allCards, pinned, pinnedOnly, query]);

  const grouped = useMemo(() => {
    const groups = [...new Set(visibleCards.map((card) => card.group))].sort((a, b) => {
      const left = SECTION_ORDER.indexOf(a);
      const right = SECTION_ORDER.indexOf(b);
      return (left === -1 ? SECTION_ORDER.length : left) - (right === -1 ? SECTION_ORDER.length : right);
    });
    return groups.map((group) => ({
      group,
      cards: visibleCards.filter((card) => card.group === group),
    }));
  }, [visibleCards]);

  const failedKeys = Object.keys(failed || {});
  const rangeLabel = formatRangeLabel(dateRange.startDate, dateRange.endDate);
  const firstLoad = loading && !allCards.length;

  const renderCard = (card) => (
    <div key={card.id} className={card.span === 2 ? 'lg:col-span-2 xl:col-span-2' : undefined}>
      <GraphCard
        card={card}
        batch={metrics}
        failed={failed}
        loading={loading}
        onDashboard={pinned.includes(card.id)}
        onToggleDashboard={(checked) => setOnDashboard(card.id, checked)}
        dashboardDisabled={prefsLoading}
        {...(websiteEligible?.includes(card.id)
          ? {
              onWebsite: onWebsite.includes(card.id),
              onToggleWebsite: (checked) => setOnWebsite(card.id, checked),
              websiteDisabled:
                websiteLoading || (websiteFull && !onWebsite.includes(card.id)),
              websiteTitle:
                websiteFull && !onWebsite.includes(card.id)
                  ? `At most ${maxWebsiteCards} graphs can be on the website - untick one first`
                  : undefined,
            }
          : {})}
      />
    </div>
  );

  return (
    <div className="flex w-full flex-col bg-slate-50/60 px-4 pb-4 pt-6 lg:h-[calc(100vh-2.75rem)] lg:overflow-hidden">
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <div className="mb-4 shrink-0 text-left">
          <h1 className="text-xl font-bold tracking-tight text-slate-800">Graphs</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Patients registered and prescriptions issued in {rangeLabel.toLowerCase()}.
          </p>
        </div>

        {/* One filter row, above everything - it scopes every card below. */}
        <DateRangeFilter
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={setDateRange}
          className="mb-3 shrink-0"
        />

        <div className="mb-4 flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search graphs"
              aria-label="Search graphs"
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-8 text-sm text-slate-700 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-200"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600"
              >
                <FaTimes className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs text-slate-400">
              {visibleCards.length} of {allCards.length} graphs
            </span>
            <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs font-medium">
              {[
                { key: false, label: 'All graphs' },
                { key: true, label: `On dashboard (${pinned.length})` },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  aria-pressed={pinnedOnly === option.key}
                  onClick={() => setPinnedOnly(option.key)}
                  className={`rounded-md px-3 py-1.5 transition-colors ${
                    pinnedOnly === option.key ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex shrink-0 items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            <FaExclamationTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {prefsError && (
          <div className="mb-4 flex shrink-0 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <FaExclamationTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>Dashboard selection: {prefsError}</span>
          </div>
        )}

        {websiteError && (
          <div className="mb-4 flex shrink-0 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <FaExclamationTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>Website selection: {websiteError}</span>
          </div>
        )}

        {!error && failedKeys.length > 0 && (
          <div className="mb-4 flex shrink-0 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <FaExclamationTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>
              {failedKeys.length} graph{failedKeys.length > 1 ? 's' : ''} could not be built: {failedKeys.join(', ')}
            </span>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto pb-6 pr-1">
          {firstLoad ? (
            <div className="py-24">
              <ThreeDotsLoading />
            </div>
          ) : (
            <>
              {!pinnedOnly && !query && (
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <StatCard
                    title="New patients"
                    value={formatNumber(totals.patients)}
                    subtitle={rangeLabel}
                    icon={<FaUserPlus />}
                    color="sky"
                  />
                  <StatCard
                    title="Prescriptions"
                    value={formatNumber(totals.prescriptions)}
                    subtitle={rangeLabel}
                    icon={<FaFilePrescription />}
                    color="violet"
                  />
                  <StatCard
                    title="Fees collected"
                    value={formatCurrency(totals.collected)}
                    subtitle={rangeLabel}
                    icon={<FaRupeeSign />}
                    color="emerald"
                  />
                  <StatCard
                    title="Average per prescription"
                    value={formatCurrency(totals.average)}
                    subtitle={rangeLabel}
                    icon={<FaReceipt />}
                    color="amber"
                  />
                </div>
              )}

              {grouped.length === 0 ? (
                <div className="mt-6 flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-white/60 py-20 text-center">
                  {pinnedOnly && !pinned.length ? (
                    <>
                      <FaRegCheckSquare className="h-5 w-5 text-slate-300" />
                      <p className="text-sm font-medium text-slate-600">No graph is on the dashboard yet</p>
                      <p className="max-w-sm text-xs text-slate-400">
                        Open <span className="font-medium text-slate-500">All graphs</span> and tick{' '}
                        <span className="font-medium text-slate-500">Show on dashboard</span> on any graph to show it
                        on the home page with data till date.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-slate-600">No graph matches “{query}”</p>
                      <p className="text-xs text-slate-400">Try a shorter word, like “city”, “doctor” or “fees”.</p>
                    </>
                  )}
                </div>
              ) : (
                grouped.map((group) => (
                  <Section key={group.group} title={group.group}>
                    {group.cards.map(renderCard)}
                  </Section>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualizeBoard;
